import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { emailNotificationApi } from '../../../api/emailNotificationApi';

const StyledStepper = styled(Stepper)(({ theme }) => ({
  padding: theme.spacing(3, 0, 5),
}));

const StepContent = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const RecipientsList = styled('div')(({ theme }) => ({
  maxHeight: 300,
  overflow: 'auto',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
}));

const PreviewBox = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  maxHeight: 300,
  overflow: 'auto',
}));

const ActionsContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(0, 2),
}));

const steps = [
  'Choisir le template',
  'Liste des apprenants',
  'Prévisualiser et envoyer'
];

const SessionEmailSendDialog = ({ open, onClose, session, customers, formation }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Charger les templates au montage et initialiser les apprenants sélectionnés
  useEffect(() => {
    if (open) {
      loadTemplates();
      // Les apprenants sélectionnés dans le tableau sont automatiquement inclus
      setSelectedCustomers(customers.map(c => c.id));
    }
  }, [open, customers]);

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await emailNotificationApi.getAllTemplates(accessToken);
      
      if (response.status === 200) {
        // Filtrer seulement les templates de type "session"
        const sessionTemplates = response.data.filter(template => template.type === 'session');
        setTemplates(sessionTemplates);
      } else {
        console.error('Erreur lors du chargement des templates');
        alert('Erreur lors du chargement des templates');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des templates:', error);
      alert('Erreur lors du chargement des templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedTemplate) {
      alert('Veuillez sélectionner un template');
      return;
    }
    
    if (activeStep === 1) {
      // Étape de prévisualisation
      generatePreview();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedTemplate('');
    setSelectedCustomers([]);
    setPreviewData(null);
    onClose();
  };

  // Ces fonctions ne sont plus nécessaires car tous les apprenants sont automatiquement sélectionnés
  // const handleCustomerToggle = (customerId) => {
  //   setSelectedCustomers(prev => 
  //     prev.includes(customerId)
  //       ? prev.filter(id => id !== customerId)
  //       : [...prev, customerId]
  //   );
  // };

  // const handleSelectAll = () => {
  //   if (selectedCustomers.length === customers.length) {
  //     setSelectedCustomers([]);
  //   } else {
  //     setSelectedCustomers(customers.map(c => c.customers_id));
  //   }
  // };

  const generatePreview = async () => {
    if (!selectedTemplate || selectedCustomers.length === 0) return;

    setPreviewLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await emailNotificationApi.getTemplateById(selectedTemplate, accessToken);
      
      if (response.status === 200) {
        const template = response.data;
        
        // Prendre le premier apprenant sélectionné pour la prévisualisation
        const firstCustomer = customers.find(c => c.id === selectedCustomers[0]);
        
        if (!firstCustomer) {
          alert('Erreur: Apprenant non trouvé');
          return;
        }

        // Simuler les données de l'apprenant et de la session pour la prévisualisation
        const previewData = {
          subject: template.subject,
          content: template.content,
          variables: {
            civilité: firstCustomer.civility || 'M.',
            nom: firstCustomer.lastname || 'Dupont',
            prénom: firstCustomer.firstname || 'Jean',
            email: firstCustomer.email || 'apprenant@example.com',
            téléphone: firstCustomer.phone || '01 23 45 67 89',
            profession: firstCustomer.profession || 'Médecin',
            spécialité: firstCustomer.specialite || 'Cardiologie',
            adresse: firstCustomer.adress || '123 Rue de la Paix',
            'code postal': firstCustomer.zip || '75001',
            ville: firstCustomer.city || 'Paris',
            'nom formation': formation?.name || formation?.formationName || '',
            'numéro de programme': formation?.program_number || '',
            'numéro de session': session?.number_session || 'S001',
            'début de session': formatDate(session?.start_date),
            'fin de session': formatDate(session?.end_date),
            'progression': `${firstCustomer.progression || 0}%`,
            'date d\'inscription': formatDateTime(firstCustomer.subscribed_at),
            'date de fin d\'unité clé': formatDate(session?.first_turn_end_date),
            'début de l\'événement': formatDateTime(session?.meeting_start_date),
            'fin de l\'événement': formatDateTime(session?.meeting_end_date),
            'lieu de l\'événement': session?.meeting_place || 'En ligne (Zoom/Teams)',
            'nom du lieu présentielle': session?.meeting_place_name || '',
            'adresse du lieu présentielle': session?.meeting_address || '',
            'formateur': session?.trainer || '',
            'lien du virtuelle': session?.meeting_link || '',
            'nom du lieu virtuelle': session?.meeting_link_name || '',
            'lien géolocalisation': session?.meeting_place || '',
            'lien questionnaire pré-formation': 'https://apteed.syndays.fr/questionnaire-public/pre/sample',
            'lien questionnaire post-formation': 'https://apteed.syndays.fr/questionnaire-public/post/sample',
            'lien questionnaire satisfaction': 'https://apteed.syndays.fr/questionnaire-public/satisfaction/sample',
            'lien questionnaire validation': 'https://apteed.syndays.fr/questionnaire-public/validation/sample'
          }
        };

        // Remplacer les variables dans le template
        let previewContent = template.content;
        let previewSubject = template.subject;
        
        Object.entries(previewData.variables).forEach(([key, value]) => {
          const regex = new RegExp(`{${key}}`, 'g');
          previewContent = previewContent.replace(regex, value);
          previewSubject = previewSubject.replace(regex, value);
        });

        setPreviewData({
          ...previewData,
          finalSubject: previewSubject,
          finalContent: previewContent,
          previewCustomer: firstCustomer
        });
        
        setActiveStep(2);
      }
    } catch (error) {
      console.error('Erreur lors de la génération de la prévisualisation:', error);
      alert('Erreur lors de la génération de la prévisualisation');
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR');
  };

  const handleSendEmail = async () => {
    if (!selectedTemplate || selectedCustomers.length === 0) return;

    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      // Récupérer les learner_session_ids pour les apprenants sélectionnés
      const learnerSessionIds = customers
        .filter(c => selectedCustomers.includes(c.id))
        .map(c => c.id);

      const response = await emailNotificationApi.sendEmailToSession(
        selectedTemplate,
        learnerSessionIds,
        accessToken
      );

      if (response.status === 200) {
        alert(`Emails envoyés avec succès ! ${response.data.sent_count} sur ${response.data.recipients_count} apprenants.`);
        handleClose();
      } else {
        alert(response.message || 'Erreur lors de l\'envoi des emails');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi des emails:', error);
      alert('Erreur lors de l\'envoi des emails');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepContent>
            <Typography variant="h6" gutterBottom>
              Sélectionnez un template d'email
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Choisissez le template que vous souhaitez utiliser pour envoyer des emails aux apprenants de cette session.
            </Typography>
            
            {templatesLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <FormControl fullWidth variant="outlined">
                <InputLabel>Template d'email</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  label="Template d'email"
                >
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedTemplate && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Template sélectionné :
                </Typography>
                {templates.find(t => t.id === selectedTemplate) && (
                  <Typography variant="body2" color="textSecondary">
                    {templates.find(t => t.id === selectedTemplate).subject}
                  </Typography>
                )}
              </Box>
            )}
          </StepContent>
        );

      case 1:
        return (
          <StepContent>
            <Typography variant="h6" gutterBottom>
              Liste des apprenants concernés
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Voici la liste des apprenants de cette session qui recevront l'email. La prévisualisation sera générée avec le premier apprenant de la liste.
            </Typography>

            <RecipientsList>
              {customers.length > 0 ? (
                <List dense>
                  {customers.map((customer, index) => (
                    <ListItem key={customer.id}>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>{index + 1}. {customer.firstname} {customer.lastname}</strong>
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="textSecondary">
                            {customer.email} - Progression: {customer.progression || 0}%
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Aucun apprenant sélectionné. Veuillez sélectionner des apprenants dans le tableau.
                </Typography>
              )}
            </RecipientsList>

            <Box mt={2}>
              <Alert severity="info">
                <Typography variant="body2">
                  {customers.length} apprenant(s) recevront l'email. Cliquez sur "Suivant" pour prévisualiser l'email.
                </Typography>
              </Alert>
            </Box>
          </StepContent>
        );

      case 2:
        return (
          <StepContent>
            <Typography variant="h6" gutterBottom>
              Prévisualisation et envoi
            </Typography>
            
            {previewLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : previewData ? (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Destinataires : {selectedCustomers.length} apprenant(s)
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Prévisualisation avec {previewData.previewCustomer.firstname} {previewData.previewCustomer.lastname} :
                </Typography>
                
                <Divider style={{ margin: '16px 0' }} />
                
                <Typography variant="subtitle2" gutterBottom>
                  Sujet :
                </Typography>
                <Typography variant="body2" paragraph style={{ fontStyle: 'italic' }}>
                  {previewData.finalSubject}
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>
                  Contenu :
                </Typography>
                <PreviewBox 
                  dangerouslySetInnerHTML={{ __html: previewData.finalContent }}
                />
                
                <Box mt={2}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      Vérifiez le contenu avant l'envoi. L'email sera envoyé à {selectedCustomers.length} apprenant(s).
                    </Typography>
                  </Alert>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Erreur lors de la génération de la prévisualisation.
              </Typography>
            )}
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Envoyer des emails - Session {session?.number_session}
      </DialogTitle>
      
      <DialogContent>
        <StyledStepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </StyledStepper>
        
        {renderStepContent(activeStep)}
      </DialogContent>
      
      <ActionsContainer>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          
          <Box display="flex" gap={1}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              disabled={loading}
            >
              Retour
            </Button>
            
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendEmail}
                disabled={loading || !previewData}
              >
                {loading ? <CircularProgress size={20} /> : `Envoyer à ${selectedCustomers.length} apprenant(s)`}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={loading || (activeStep === 0 && !selectedTemplate)}
              >
                {activeStep === 1 && previewLoading ? <CircularProgress size={20} /> : 'Suivant'}
              </Button>
            )}
          </Box>
        </Box>
      </ActionsContainer>
    </Dialog>
  );
};

SessionEmailSendDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
  customers: PropTypes.array.isRequired,
  formation: PropTypes.object.isRequired,
};

export default SessionEmailSendDialog; 